import { AnimatePresence, motion } from 'framer-motion';
import { Download, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useInstallPrompt } from '@/lib/useInstallPrompt';
import { fa } from '@/i18n/fa';

/**
 * A tasteful, dismissible "add to home screen" card — replaces the
 * browser's default install infobar with Persian copy that matches the
 * design system. Only renders when the browser has fired
 * `beforeinstallprompt` and the user hasn't already dismissed it.
 */
export function InstallPrompt() {
  const { isInstallable, promptInstall, dismiss } = useInstallPrompt();

  return (
    <AnimatePresence>
      {isInstallable && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-4 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-40 mx-auto max-w-sm rounded-card border border-border bg-surface p-4 shadow-floating sm:inset-x-auto sm:end-6 sm:bottom-6"
          role="dialog"
          aria-label={fa.pwa.installTitle}
        >
          <div className="flex items-start gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
              <Sparkles className="size-5" />
            </span>
            <div className="flex flex-1 flex-col gap-1">
              <p className="text-sm font-bold text-text">{fa.pwa.installTitle}</p>
              <p className="text-xs text-text-muted">{fa.pwa.installDescription}</p>
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label={fa.common.close}
              className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-subtle transition-colors hover:bg-surface-raised hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
            >
              <X className="size-4" />
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" className="flex-1 gap-1.5" onClick={() => void promptInstall()}>
              <Download className="size-4" />
              {fa.pwa.install}
            </Button>
            <Button size="sm" variant="outline" className="flex-1" onClick={dismiss}>
              {fa.pwa.dismiss}
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
